import { useCallback, useContext, useEffect, useState } from 'react';
import UpcomingContext from '../context';
import { addCampaignTag, clearSubscriptions, getCampaignStatus, getCampaignTags, removeCampaignTag, } from '../services';
import { UpcomingType } from '../types';
export const useUpcomingPushMessages = (upcomingCampaigns, appDomain, notificationTagSuffix = '', onRegister, checkAccess, registerUser, isEarlyAccess = false) => {
    const upcomingContext = useContext(UpcomingContext);
    if (upcomingContext === undefined) {
        throw new Error('useUpcomingPushMessages must be used within UpcomingProvider.');
    }
    const { dispatch, earlyCampaigns, regularCampaigns } = upcomingContext;
    const [pendingCampaignId, setPendingCampaignId] = useState(null);
    const tagSuffix = isEarlyAccess
        ? `_early_access${notificationTagSuffix}`
        : notificationTagSuffix;
    useEffect(() => {
        getCampaignTags(appDomain, tagSuffix).then(subscribedList => {
            const subscribedById = subscribedList.reduce((obj, id) => ({
                ...obj,
                [id]: true,
            }), {});
            const type = isEarlyAccess ? UpcomingType.SET_EARLY_ACCESS : UpcomingType.SET_REGULAR;
            dispatch({ type, payload: subscribedById });
        });
    }, [appDomain, isEarlyAccess, tagSuffix]);
    useEffect(() => {
        if (upcomingCampaigns && upcomingCampaigns.length) {
            clearSubscriptions(upcomingCampaigns, appDomain, tagSuffix);
        }
    }, [upcomingCampaigns, appDomain, tagSuffix]);
    const toggleReminder = useCallback(async (campaignId) => {
        const status = await getCampaignStatus(campaignId, appDomain, tagSuffix);
        const actionFunction = status ? removeCampaignTag : addCampaignTag;
        const result = await actionFunction(campaignId, appDomain, tagSuffix);
        return status ? !result : result;
    }, [appDomain, tagSuffix]);
    const subscribeToReminder = useCallback(async (campaignId) => {
        try {
            const accessGranted = await checkAccess();
            if (!accessGranted) {
                setPendingCampaignId(campaignId);
                return;
            }
            const result = await toggleReminder(campaignId);
            const type = isEarlyAccess
                ? UpcomingType.ADD_EARLY_ACCESS
                : UpcomingType.ADD_REGULAR;
            dispatch({ type, payload: { [campaignId]: result } });
        }
        catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
        }
    }, [checkAccess, isEarlyAccess, toggleReminder]);
    const registerToReminder = useCallback(async ({ data: campaignId, errorMessage, successMessage, }) => {
        const registered = await registerUser();
        if (!registered) {
            onRegister(false, errorMessage);
            return;
        }
        try {
            const result = await toggleReminder(campaignId);
            const type = isEarlyAccess
                ? UpcomingType.ADD_EARLY_ACCESS
                : UpcomingType.ADD_REGULAR;
            dispatch({ type, payload: { [campaignId]: result } });
        }
        catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
        }
        setPendingCampaignId(null);
        onRegister(true, successMessage);
    }, [isEarlyAccess, onRegister, registerUser, toggleReminder]);
    const clearPendingCampaignId = useCallback(() => {
        setPendingCampaignId(null);
    }, []);
    const subscribedCampaigns = isEarlyAccess ? earlyCampaigns : regularCampaigns;
    return {
        subscribeToReminder,
        subscribedCampaigns,
        registerToReminder,
        pendingCampaignId,
        clearPendingCampaignId,
    };
};
//# sourceMappingURL=use-upcoming-push-messages.js.map